import { SocketStateModel } from '@e3dc-react/shell/Hooks/use-websocket-api'
import React, { Context, useMemo } from 'react'
import { EmergencyPowerSettingsModel, useLiveEmergencyPower } from 'src/Libs/emergency-power'
import { StorageStatusModel, useLiveStorageStatus } from 'src/Libs/storage'
import { useLiveWallboxStatus, useLiveWallboxCharging, useWallboxHardwareByStorage } from 'src/Libs/wallboxes/hooks'
import { WallboxChargingModel, WallboxStatusModel } from 'src/Libs/wallboxes/models'
import { useTypedSelector } from 'src/State/RootReducer'
import { useFarmContext } from '../Farm'
import { isFlowTheme } from 'src/Libs/utils'

interface SocketContextModel {
  storageSocketState: SocketStateModel<StorageStatusModel>
  wallboxSocketState: SocketStateModel<WallboxStatusModel[]>
  wallboxChargingSocketState: SocketStateModel<WallboxChargingModel[]>
  batterySocketState: SocketStateModel<StorageStatusModel>
  emergencyPowerBatterySocketState: SocketStateModel<EmergencyPowerSettingsModel>
  emergencyPowerStorageSocketState: SocketStateModel<EmergencyPowerSettingsModel>
}

const defaultContext: {
  // solarSocketState: SolarSocketState
  storageSocketState: SocketStateModel<StorageStatusModel>
  wallboxSocketState: SocketStateModel<WallboxStatusModel[]>
  wallboxChargingSocketState: SocketStateModel<WallboxChargingModel[]>
  batterySocketState: SocketStateModel<StorageStatusModel>
  emergencyPowerBatterySocketState: SocketStateModel<EmergencyPowerSettingsModel>
  emergencyPowerStorageSocketState: SocketStateModel<EmergencyPowerSettingsModel>
} = {
  // solarSocketState: { connected: false },
  storageSocketState: { connected: false },
  wallboxSocketState: { connected: false },
  wallboxChargingSocketState: { connected: false },
  batterySocketState: { connected: false },
  emergencyPowerBatterySocketState: { connected: false },
  emergencyPowerStorageSocketState: { connected: false },
}

const SocketContext = React.createContext(defaultContext)

export interface UseWebsocketsProps {
  children?: React.ReactNode
}

const SystemDataProvider: React.FC<UseWebsocketsProps> = ({ children }) => {
  const storageId = useTypedSelector(state => state.storage.activeStorage)
  const batteryId = useTypedSelector(state => state.storage.activeStorageBattery)
  const { data: wallboxHardware } = useWallboxHardwareByStorage()
  const hasWallbox = wallboxHardware?.length > 0

  const storageSocketState = useLiveStorageStatus(storageId)
  const wallboxSocketState = useLiveWallboxStatus(storageId)
  const wallboxChargingSocketState = useLiveWallboxCharging(storageId, hasWallbox)
  /** In some cases the battery is a storage. */
  const batterySocketState = useLiveStorageStatus(batteryId, batteryId === storageId)
  const emergencyPowerStorageSocketState = useLiveEmergencyPower(batteryId, batteryId === storageId)
  /** For farms the request for the battery emergency state should use the masters storageId */
  const { isFarm, farmMaster, isLoading: isFarmLoading } = useFarmContext()
  const emergencyBatteryStorageIdToUse = isFarm ? farmMaster?.storageId : storageId
  const emergencyPowerBatterySocketState = useLiveEmergencyPower(emergencyBatteryStorageIdToUse, isFarmLoading)

  const value = useMemo(
    () => ({
      // solarSocketState: solarSocket,
      storageSocketState,
      wallboxSocketState: {
        ...wallboxSocketState,
        data: isFlowTheme ? wallboxSocketState.data?.filter(wallbox => wallbox.wallboxId !== 'VIRTUAL') : wallboxSocketState.data,
      },
      wallboxChargingSocketState: {
        ...wallboxChargingSocketState,
        data: isFlowTheme ? wallboxChargingSocketState.data?.filter(wallbox => wallbox.wallboxId !== 'VIRTUAL') : wallboxChargingSocketState.data,
      },
      batterySocketState,
      emergencyPowerBatterySocketState,
      emergencyPowerStorageSocketState,
    }),
    [
      batterySocketState,
      emergencyPowerBatterySocketState,
      emergencyPowerStorageSocketState,
      storageSocketState,
      wallboxChargingSocketState,
      wallboxSocketState,
    ]
  )

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
}

const useSocketConnectionContext = (): Context<SocketContextModel> => {
  if (SocketContext === undefined) {
    throw new Error('useCountDispatch must be used within a CountProvider')
  }

  return SocketContext
}

export { SystemDataProvider, useSocketConnectionContext }
